import { fetchText, fetchWithRetry, HttpError } from '../lib/fetchJson';
import type { DailyStats, USGSGaugeData, USGSSeriesPoint } from '../types/usgs';

export class USGSNoDataError extends Error {
  constructor() {
    super('No real-time data available for this gauge.');
    this.name = 'USGSNoDataError';
  }
}

const ALL_PARAMETERS = ['00065', '00060', '00010'] as const;

/** Parameter codes each site is known to support, so retries do not ask for series the site lacks (USGSService). */
const knownParameterCodes = new Map<string, string[]>();

interface USGSResponse {
  value?: {
    timeSeries?: {
      variable?: { variableCode?: { value: string }[] };
      values?: { value?: { value: string; dateTime: string }[] }[];
      sourceInfo?: { siteName?: string };
    }[];
  };
}

export function parseUSGSResponse(data: USGSResponse): USGSGaugeData {
  const out: USGSGaugeData = { siteName: null, height: [], discharge: [], temperature: [], supportedCodes: [] };
  for (const s of data?.value?.timeSeries ?? []) {
    const code = s.variable?.variableCode?.[0]?.value;
    const target: USGSSeriesPoint[] | null =
      code === '00065' ? out.height : code === '00060' ? out.discharge : code === '00010' ? out.temperature : null;
    if (!target || !code) continue;
    if (!out.supportedCodes.includes(code)) out.supportedCodes.push(code);
    if (s.sourceInfo?.siteName) out.siteName = s.sourceInfo.siteName;
    for (const block of s.values ?? []) {
      for (const pt of block.value ?? []) {
        const v = Number.parseFloat(pt.value);
        if (!Number.isFinite(v) || v < -900_000) continue;
        const d = new Date(pt.dateTime);
        if (Number.isNaN(d.getTime())) continue;
        target.push({ date: d, value: v });
      }
    }
  }
  for (const arr of [out.height, out.discharge, out.temperature]) arr.sort((a, b) => a.date.getTime() - b.date.getTime());
  return out;
}

/**
 * Seven days of instantaneous values. Retries 502/503/504 up to three attempts
 * with 0.5 s then 1 s backoff; a "no sites" body means the site has no data.
 */
export async function fetchGaugeData(siteNo: string, signal?: AbortSignal): Promise<USGSGaugeData> {
  const codes = knownParameterCodes.get(siteNo) ?? [...ALL_PARAMETERS];
  const url = `https://waterservices.usgs.gov/nwis/iv/?sites=${encodeURIComponent(siteNo)}&parameterCd=${codes.join(',')}&period=P7D&format=json`;
  const res = await fetchWithRetry(url, { signal, timeoutMs: 15_000, retryOn: [502, 503, 504], maxAttempts: 3 });
  const text = await res.text();
  if (res.status !== 200) {
    if (text.toLowerCase().includes('no sites')) throw new USGSNoDataError();
    throw new HttpError(res.status, text);
  }
  let data: USGSResponse;
  try {
    data = JSON.parse(text) as USGSResponse;
  } catch {
    if (text.toLowerCase().includes('no sites')) throw new USGSNoDataError();
    throw new Error('USGS returned an unreadable response.');
  }
  const parsed = parseUSGSResponse(data);
  if (parsed.supportedCodes.length) {
    knownParameterCodes.set(siteNo, ALL_PARAMETERS.filter((c) => parsed.supportedCodes.includes(c)));
  }
  return parsed;
}

/** RDB parser for daily percentiles of gage height on a calendar day (USGSStatisticsService). */
export function parseDailyStats(rdb: string, month: number, day: number): DailyStats | null {
  const lines = rdb.split('\n').filter((l) => !l.startsWith('#') && l.trim() !== '');
  if (lines.length < 3) return null;
  const headers = lines[0].split('\t');
  const idx = (k: string) => headers.indexOf(k);
  const m = idx('month_nu');
  const d = idx('day_nu');
  const p25 = idx('p25_va');
  const p50 = idx('p50_va');
  const p75 = idx('p75_va');
  if ([m, d, p25, p50, p75].some((i) => i < 0)) return null;
  const maxIdx = Math.max(m, d, p25, p50, p75);
  for (const line of lines.slice(2)) {
    const cols = line.split('\t');
    if (cols.length <= maxIdx) continue;
    if (Number.parseInt(cols[m], 10) !== month || Number.parseInt(cols[d], 10) !== day) continue;
    const num = (s: string) => {
      const v = Number.parseFloat(s);
      return Number.isFinite(v) ? v : null;
    };
    return { p25: num(cols[p25]), p50: num(cols[p50]), p75: num(cols[p75]) };
  }
  return null;
}

export async function fetchDailyStats(siteNo: string, date: Date = new Date(), signal?: AbortSignal): Promise<DailyStats | null> {
  const url = `https://waterservices.usgs.gov/nwis/stat/?format=rdb&sites=${encodeURIComponent(siteNo)}&parameterCd=00065&statReportType=daily&statType=mean,p25,p50,p75`;
  const text = await fetchText(url, { signal, timeoutMs: 15_000 });
  return parseDailyStats(text, date.getMonth() + 1, date.getDate());
}

/** Percentile sentence shown in Site Details. */
export function percentileText(current: number, stats: DailyStats): string {
  if (stats.p75 != null && current >= stats.p75) return 'Above 75th percentile (historically high)';
  if (stats.p50 != null && current >= stats.p50) return '50th–75th percentile (above average)';
  if (stats.p25 != null && current >= stats.p25) return '25th–50th percentile (below average)';
  return 'Below 25th percentile (historically low)';
}
